import { useLayoutEffect, useRef } from "react";

const INERT_MARK = "data-registration-inert";
const INERT_ARIA_MARK = "data-registration-inert-aria";

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const isElementVisible = (element) => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  if (
    element.hasAttribute("disabled") ||
    element.hasAttribute("inert") ||
    element.closest("[inert]") ||
    element.getAttribute("aria-hidden") === "true"
  ) {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") {
    return false;
  }
  return element.getClientRects().length > 0;
};

export const getFocusableElements = (container) => {
  if (!container) {
    return [];
  }
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    isElementVisible
  );
};

export const focusAdjacentControl = (
  event,
  selector = "button:not([disabled])",
  onMove
) => {
  if (
    event.key !== "ArrowDown" &&
    event.key !== "ArrowUp" &&
    event.key !== "ArrowLeft" &&
    event.key !== "ArrowRight"
  ) {
    return;
  }

  const root = event.currentTarget;
  if (!(root instanceof HTMLElement)) {
    return;
  }

  const items = Array.from(root.querySelectorAll(selector)).filter(
    isElementVisible
  );
  const currentIndex = items.indexOf(document.activeElement);
  if (currentIndex < 0 || items.length === 0) {
    return;
  }

  event.preventDefault();
  const delta =
    event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : -1;
  const nextIndex = (currentIndex + delta + items.length) % items.length;
  items[nextIndex]?.focus();
  onMove?.(items[nextIndex]);
};

const hideBackgroundFromAssistiveTech = (container) => {
  const marked = [];
  let current = container;

  while (current && current !== document.body) {
    const parent = current.parentElement;
    if (!parent) {
      break;
    }

    Array.from(parent.children).forEach((sibling) => {
      if (sibling === current || !(sibling instanceof HTMLElement)) {
        return;
      }
      if (sibling.hasAttribute(INERT_MARK)) {
        return;
      }

      sibling.setAttribute(INERT_MARK, "true");
      sibling.setAttribute("inert", "");
      if (!sibling.hasAttribute("aria-hidden")) {
        sibling.setAttribute("aria-hidden", "true");
        sibling.setAttribute(INERT_ARIA_MARK, "1");
      }
      marked.push(sibling);
    });

    current = parent;
  }

  return marked;
};

const restoreBackgroundForAssistiveTech = (marked) => {
  marked.forEach((element) => {
    element.removeAttribute("inert");
    element.removeAttribute(INERT_MARK);
    if (element.getAttribute(INERT_ARIA_MARK) === "1") {
      element.removeAttribute("aria-hidden");
    }
    element.removeAttribute(INERT_ARIA_MARK);
  });
};

/**
 * Traps Tab inside an open dialog, focuses an initial control, and restores
 * focus to the opener when the dialog closes.
 */
export function useFocusTrap({
  isOpen,
  containerRef,
  initialFocusRef,
  onEscape,
  restoreFocus = true,
}) {
  const previousFocusRef = useRef(null);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const container = containerRef?.current;
    if (!container) {
      return undefined;
    }

    if (!container.hasAttribute("tabindex")) {
      container.setAttribute("tabindex", "-1");
    }

    const markedBackground = hideBackgroundFromAssistiveTech(container);

    const focusInitial = () => {
      const initial = initialFocusRef?.current;
      if (initial instanceof HTMLElement) {
        initial.focus();
        return;
      }
      const focusable = getFocusableElements(container);
      (focusable[0] || container).focus();
    };

    focusInitial();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (typeof onEscapeRef.current === "function") {
          event.preventDefault();
          event.stopPropagation();
          onEscapeRef.current();
        }
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && (active === last || !container.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      restoreBackgroundForAssistiveTech(markedBackground);
      if (!restoreFocus) {
        return;
      }
      const previous = previousFocusRef.current;
      if (previous instanceof HTMLElement) {
        previous.focus();
      }
    };
  }, [isOpen, containerRef, initialFocusRef, restoreFocus]);
}
